import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { resolveMediaUrl } from "@/components/common/MediaFrame";
import { DemoBadge } from "@/components/common/States";
import JobDetailModal from "@/components/common/JobDetailModal";
import { getReadableAssetTitle, getTechnicalFilename, isTechnicalName } from "@/domain/assetDisplay";
import { downloadAsset } from "@/components/common/ImageActionBar";
import { useStudio, useLookups } from "@/store/StudioContext";
import { t } from "@/i18n";
import {
  X,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  PanelRightClose,
  PanelRightOpen,
  Edit2,
  Check,
  Copy,
  FileText,
  Download,
  Sparkles,
  ExternalLink,
  BookmarkPlus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Code,
} from "lucide-react";
import { toast } from "sonner";

function formatShortId(id) {
  if (!id) return "";
  if (id.length <= 16) return id;
  const prefix = id.startsWith("ast_") ? "ast_" : (id.startsWith("upload_") ? "upload_" : "");
  const rest = id.slice(prefix.length);
  return `${prefix}${rest.slice(0, 6)}…${rest.slice(-4)}`;
}

/**
 * AssetDetailModal — Visor superpuesto grande para imágenes y vídeos (Fase 2B.4B).
 *
 * Características:
 * - Prioridad a la imagen en pantalla completa (max-w-[96vw], h-[92vh]), sin recortes (fit contain).
 * - Zoom (1x a 4x), paneo/desplazamiento al estar ampliada y restablecer ajuste ("Ajustar").
 * - Panel lateral de información plegable para maximizar el área visible.
 * - Soporte de nombre legible resuelto y edición inline de nombre persistida con ASSET_UPDATE.
 * - Muestra nombre de archivo técnico original y UUID como información secundaria copiable.
 * - Acciones integradas: Ver detalles de generación (JobDetailModal), Descargar, Usar como referencia, Abrir en Editor.
 * - Cierre con botón X y tecla Escape.
 */
export default function AssetDetailModal({
  asset,
  open,
  onClose,
  projectId = null,
  onUseAsReference = null,
  onOpenInEditor = null,
  onOpenUpscale = null,
  onDelete = null,
  testId = "gallery-detail",
}) {
  const { state, dispatch } = useStudio();
  const { projectById, sceneById, assetById } = useLookups();

  // Estados de visualización
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Panel lateral de información plegable
  const [infoOpen, setInfoOpen] = useState(true);
  const [techInfoOpen, setTechInfoOpen] = useState(false);
  const [naturalDims, setNaturalDims] = useState(null);

  // Edición inline del nombre
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState("");

  // Modal anidado de detalles de generación
  const [viewJobDetail, setViewJobDetail] = useState(null);

  // Restablecer zoom, pan y dimensiones naturales al cambiar de asset o al abrir
  useEffect(() => {
    if (open) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setIsEditingName(false);
      setTechInfoOpen(false);
      setNaturalDims(null);
    }
  }, [open, asset?.id]);

  // Buscar únicamente el trabajo productor explícito de este asset.
  // No usar como sustituto una tarea que utilizó esta imagen como origen/referencia ni la última tarea.
  const linkedJob = useMemo(() => {
    if (!asset || asset.source === "upload") return null;
    if (!state.jobs || !Array.isArray(state.jobs)) return null;

    return state.jobs.find((j) => {
      // 1. Coincidencia por resultado explícito del job
      if (j.outputAssetId && j.outputAssetId === asset.id) return true;
      if (j.resultAssetId && j.resultAssetId === asset.id) return true;
      if (Array.isArray(j.resultAssetIds) && j.resultAssetIds.includes(asset.id)) return true;

      // 2. Si el asset declara explícitamente su jobId productor
      if (asset.jobId && (j.id === asset.jobId || j.backendJobId === asset.jobId)) {
        // Asegurar que no sea una tarea donde el asset fue mero origen/insumo
        const inputAssetId = j.sourceAssetId || j.assetId || j.params?.source_asset_id;
        if (inputAssetId && inputAssetId === asset.id && j.outputAssetId && j.outputAssetId !== asset.id) {
          return false;
        }
        return true;
      }
      return false;
    }) || null;
  }, [asset, state.jobs]);

  if (!asset) return null;

  const currentProjectId = projectId || asset.projectId || null;
  const currentScene = sceneById[asset.sceneId] || null;
  const readableTitle = getReadableAssetTitle({ asset, scene: currentScene, t });
  const technicalName = getTechnicalFilename(asset);
  const mediaUrl = resolveMediaUrl(asset.url);
  const isVideo = asset.type === "video";

  // Dimensiones verificadas (procedencia demostrable: decodificación Pillow/ffprobe en backend o naturalWidth/naturalHeight del original cargado)
  const verifiedWidth = asset.width || naturalDims?.width || null;
  const verifiedHeight = asset.height || naturalDims?.height || null;
  const verifiedDimensionsDisplay = (verifiedWidth && verifiedHeight)
    ? `${verifiedWidth} × ${verifiedHeight} px`
    : (t.gallery?.notAvailable || "No disponible");

  // Manejo de Zoom
  const handleZoomIn = () => {
    setZoom((z) => Math.min(4, Math.round((z + 0.25) * 100) / 100));
  };

  const handleZoomOut = () => {
    setZoom((z) => {
      const nextZ = Math.max(1, Math.round((z - 0.25) * 100) / 100);
      if (nextZ === 1) setPan({ x: 0, y: 0 });
      return nextZ;
    });
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    if (isVideo) return; // Permitir scroll nativo o controles en video
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.2 : -0.2;
    setZoom((z) => {
      const nextZ = Math.min(4, Math.max(1, Math.round((z + delta) * 100) / 100));
      if (nextZ === 1) setPan({ x: 0, y: 0 });
      return nextZ;
    });
  };

  // Manejo de Arrastre / Pan
  const handleMouseDown = (e) => {
    if (zoom <= 1 || isVideo) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || zoom <= 1) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPan({
      x: dragStartRef.current.panX + dx,
      y: dragStartRef.current.panY + dy,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoom <= 1 || isVideo || e.touches.length !== 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    dragStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      panX: pan.x,
      panY: pan.y,
    };
  };

  const handleTouchMove = (e) => {
    if (!isDragging || zoom <= 1 || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const dx = touch.clientX - dragStartRef.current.x;
    const dy = touch.clientY - dragStartRef.current.y;
    setPan({
      x: dragStartRef.current.panX + dx,
      y: dragStartRef.current.panY + dy,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleDoubleClick = () => {
    if (isVideo) return;
    if (zoom > 1) {
      handleResetZoom();
    } else {
      setZoom(2);
    }
  };

  // Manejo de Edición de Nombre
  const startEditing = () => {
    const raw = asset.label || asset.name || "";
    setEditName(!isTechnicalName(raw) ? raw : "");
    setIsEditingName(true);
  };

  const saveEditing = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== asset.label) {
      dispatch({
        type: "ASSET_UPDATE",
        payload: {
          id: asset.id,
          patch: { label: trimmed },
        },
      });
      toast.success("Nombre de la imagen actualizado");
    }
    setIsEditingName(false);
  };

  const cancelEditing = () => {
    setIsEditingName(false);
  };

  // Manejo de Usar como Referencia
  const handleUseReference = () => {
    if (onUseAsReference) {
      onUseAsReference(asset);
    } else if (currentProjectId) {
      // Crear referencia en el proyecto actual
      dispatch({
        type: "REFERENCE_CREATE",
        payload: {
          projectId: currentProjectId,
          assetId: asset.id,
          label: `@${asset.label || "referencia"}`,
          scope: "project",
        },
      });
      toast.success("Recurso añadido como referencia del proyecto");
    } else {
      toast.info("Añadido a referencias");
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent
          data-testid={testId}
          hideCloseButton={true}
          className="max-w-[96vw] w-[96vw] h-[92vh] max-h-[92vh] border-[#303030] bg-[#121212] p-0 text-[#f4f1ec] flex flex-col overflow-hidden shadow-2xl rounded-xl"
        >
          <DialogTitle className="sr-only">Detalles del recurso</DialogTitle>
          {/* ── Encabezado superior ── */}
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-[#262626] bg-[#171717] px-4">
            {/* Título legible con edición inline */}
            <div className="flex items-center gap-3 min-w-0 mr-4">
              {isEditingName ? (
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEditing();
                      if (e.key === "Escape") cancelEditing();
                    }}
                    autoFocus
                    data-testid="asset-title-input"
                    className="h-8 rounded border border-[#f5b544] bg-[#222] px-2.5 text-sm text-[#f4f1ec] focus:outline-none focus:ring-1 focus:ring-[#f5b544]"
                  />
                  <button
                    type="button"
                    onClick={saveEditing}
                    className="inline-flex h-8 w-8 items-center justify-center rounded bg-[#f5b544] text-[#121212] hover:bg-[#e5a534]"
                    title="Guardar nombre"
                    data-testid="asset-title-save-btn"
                  >
                    <Check size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="inline-flex h-8 w-8 items-center justify-center rounded bg-[#2a2a2a] text-[#a3a3a3] hover:text-white"
                    title="Cancelar"
                  >
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 truncate">
                  <h2
                    data-testid="asset-readable-title"
                    className="truncate font-display text-base font-semibold text-[#f4f1ec] cursor-pointer hover:text-[#f5b544] transition-colors"
                    onClick={startEditing}
                    title={technicalName ? `${t.editor?.originalFile || "Archivo original"}: ${technicalName}` : "Pulsar para renombrar"}
                  >
                    {readableTitle}
                  </h2>
                  <button
                    type="button"
                    onClick={startEditing}
                    className="text-[#737373] hover:text-[#f5b544] p-1 transition-colors"
                    title="Editar nombre"
                    data-testid="asset-rename-btn"
                  >
                    <Edit2 size={13} />
                  </button>
                  {asset.isDemo && <DemoBadge />}
                  {asset.source === "simulation" && <DemoBadge label={t.tasks.simTag} />}
                </div>
              )}
            </div>

            {/* Controles de visualización y cierre */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Controles de Zoom para imágenes */}
              {!isVideo && (
                <div className="flex items-center rounded-lg border border-[#303030] bg-[#1c1c1c] p-0.5 text-xs text-[#a3a3a3]">
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    disabled={zoom <= 1}
                    className="p-1.5 hover:text-white disabled:opacity-40 transition-colors"
                    title="Reducir zoom (-)"
                    data-testid="asset-zoom-out"
                  >
                    <ZoomOut size={14} />
                  </button>
                  <span className="w-12 text-center font-mono font-medium text-[#d8d4cd]">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={handleZoomIn}
                    disabled={zoom >= 4}
                    className="p-1.5 hover:text-white disabled:opacity-40 transition-colors"
                    title="Aumentar zoom (+)"
                    data-testid="asset-zoom-in"
                  >
                    <ZoomIn size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={handleResetZoom}
                    disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                    className="flex items-center gap-1 border-l border-[#303030] px-2 py-1 text-[11px] hover:text-white disabled:opacity-40 transition-colors"
                    title="Ajustar a pantalla"
                    data-testid="asset-zoom-reset"
                  >
                    <RotateCcw size={12} />
                    <span>Ajustar</span>
                  </button>
                </div>
              )}

              {/* Botón para plegar/desplegar información */}
              <button
                type="button"
                onClick={() => setInfoOpen((o) => !o)}
                className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                  infoOpen
                    ? "border-[#303030] bg-[#1c1c1c] text-[#d8d4cd] hover:border-[#404040]"
                    : "border-[#f5b544]/40 bg-[#f5b544]/10 text-[#f5b544] hover:border-[#f5b544]"
                }`}
                title={infoOpen ? "Ocultar panel de información" : "Mostrar panel de información"}
                data-testid="asset-toggle-info-btn"
              >
                {infoOpen ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
                <span className="hidden sm:inline">{infoOpen ? "Plegar info" : "Ver info"}</span>
              </button>

              {/* Botón de cierre */}
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-[#a3a3a3] hover:bg-[#262626] hover:text-white transition-colors"
                title="Cerrar (Escape)"
                data-testid="asset-modal-close-btn"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* ── Cuerpo principal ── */}
          <div className="flex flex-1 overflow-hidden min-h-0">
            {/* Área principal del visor (Imagen / Vídeo) */}
            <div
              className="relative flex flex-1 items-center justify-center overflow-hidden bg-[#0a0a0a] select-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onDoubleClick={handleDoubleClick}
              style={{
                cursor: isVideo ? "default" : zoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
              }}
            >
              <div
                className="flex items-center justify-center h-full w-full p-4 pointer-events-none"
                style={{
                  transform: isVideo ? "none" : `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: isDragging ? "none" : "transform 0.15s ease-out",
                }}
              >
                {isVideo ? (
                  <video
                    key={asset.id}
                    data-testid="asset-detail-video"
                    src={mediaUrl}
                    controls
                    autoPlay
                    playsInline
                    onLoadedMetadata={(e) => {
                      const vid = e.currentTarget;
                      if (vid.videoWidth && vid.videoHeight) {
                        setNaturalDims({ width: vid.videoWidth, height: vid.videoHeight });
                      }
                    }}
                    className="max-h-full max-w-full object-contain rounded pointer-events-auto"
                  />
                ) : (
                  <img
                    key={asset.id}
                    data-testid="asset-detail-image"
                    src={mediaUrl}
                    alt={asset.label || ""}
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (img.naturalWidth && img.naturalHeight) {
                        setNaturalDims({ width: img.naturalWidth, height: img.naturalHeight });
                      }
                    }}
                    className="max-h-full max-w-full object-contain rounded shadow-2xl pointer-events-none"
                    draggable={false}
                  />
                )}
              </div>

              {/* Indicador flotante cuando está ampliado */}
              {!isVideo && zoom > 1 && (
                <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full border border-black/40 bg-black/70 px-3 py-1 text-xs text-[#d8d4cd] backdrop-blur select-none">
                  <span>Zoom: {Math.round(zoom * 100)}%</span>
                  <span className="text-[#666]">·</span>
                  <span className="text-[#888]">Arrastra para explorar</span>
                  <span className="text-[#666]">·</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetZoom();
                    }}
                    className="text-[#f5b544] hover:underline"
                  >
                    Restablecer
                  </button>
                </div>
              )}
            </div>

            {/* ── Panel lateral de Información (Plegable) ── */}
            {infoOpen && (
              <div
                className="w-[320px] max-w-[320px] shrink-0 border-l border-[#262626] bg-[#161616] p-4 flex flex-col justify-between overflow-y-auto"
                data-testid="asset-info-panel"
              >
                <div className="flex flex-col gap-4">
                  {/* Resumen del Asset */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[#a3a3a3] mb-2">
                      Detalles del recurso
                    </h3>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                      {/* Tipo */}
                      <dt className="text-[#737373]">{t.gallery?.type || "Tipo"}:</dt>
                      <dd className="capitalize text-[#d8d4cd]">{asset.type === "video" ? "Vídeo" : "Imagen"}</dd>

                      {/* Dimensiones estrictas verificadas en píxeles */}
                      <dt className="text-[#737373]">{t.gallery?.realDimensions || t.gallery?.dimensions || "Dimensiones reales"}:</dt>
                      <dd className="font-mono text-[#d8d4cd]" data-testid="asset-dimensions">
                        {verifiedDimensionsDisplay}
                      </dd>

                      {/* Relación de aspecto independiente */}
                      <dt className="text-[#737373]">{t.gallery?.aspectRatio || "Relación aspecto"}:</dt>
                      <dd className="text-[#d8d4cd]" data-testid="asset-aspect-ratio">
                        {asset.aspectRatio || (verifiedWidth && verifiedHeight ? `${verifiedWidth}:${verifiedHeight}` : null) || (t.gallery?.notAvailable || "No disponible")}
                      </dd>

                      {/* Proyecto */}
                      <dt className="text-[#737373]">Proyecto:</dt>
                      <dd className="truncate text-[#d8d4cd]">
                        {projectById[asset.projectId]?.name || t.common?.noProject || "Sin proyecto"}
                      </dd>

                      {/* Escena */}
                      <dt className="text-[#737373]">Escena:</dt>
                      <dd className="truncate text-[#d8d4cd]">{currentScene?.title || "—"}</dd>

                      {/* Origen */}
                      <dt className="text-[#737373]">Origen:</dt>
                      <dd className="text-[#d8d4cd] capitalize" data-testid="asset-source-value">
                        {asset.source === "upload"
                          ? (t.gallery?.sourceUpload || "Subida")
                          : (asset.source === "generation"
                              ? (t.gallery?.sourceGeneration || "Generación")
                              : (asset.source || t.gallery?.sourceUnknown || "No especificado"))}
                      </dd>
                    </dl>

                    {/* Aviso contextual diferenciado según el origen real cuando no existe tarea productora localizable */}
                    {!linkedJob && (
                      <div className="mt-3 rounded-lg border border-[#2a2a2a] bg-[#141414] p-2.5 text-xs text-[#a3a3a3]" data-testid="asset-source-banner">
                        {asset.source === "upload" ? (
                          <>
                            <span className="font-semibold text-[#d8d4cd] block mb-0.5" data-testid="asset-source-title">
                              {t.gallery?.uploadedAssetTitle || "Imagen subida"}
                            </span>
                            <span className="text-[11px] leading-relaxed" data-testid="asset-source-notice">
                              {t.gallery?.uploadedAssetNotice || "Este recurso fue subido manualmente y no procede de una tarea de generación."}
                            </span>
                          </>
                        ) : asset.source === "generation" ? (
                          <>
                            <span className="font-semibold text-[#d8d4cd] block mb-0.5" data-testid="asset-source-title">
                              {t.gallery?.generationJobNotFoundTitle || "Generación (tarea no localizable)"}
                            </span>
                            <span className="text-[11px] leading-relaxed" data-testid="asset-source-notice">
                              {t.gallery?.generationJobNotFoundNotice || "Este recurso procede de una generación, pero no se encontró un trabajo productor asociado en el historial local."}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="font-semibold text-[#d8d4cd] block mb-0.5" data-testid="asset-source-title">
                              {t.gallery?.unknownSourceTitle || "Origen no especificado"}
                            </span>
                            <span className="text-[11px] leading-relaxed" data-testid="asset-source-notice">
                              {t.gallery?.unknownSourceNotice || "No se dispone de información sobre el origen de este recurso."}
                            </span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Sección técnica plegable (cerrada por defecto) */}
                    <div className="mt-3 border-t border-[#262626] pt-2.5">
                      <button
                        type="button"
                        data-testid="asset-tech-info-toggle"
                        onClick={() => setTechInfoOpen((o) => !o)}
                        className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wider text-[#a3a3a3] hover:text-[#f4f1ec] transition-colors py-1"
                      >
                        <span className="flex items-center gap-1.5">
                          <Code size={13} />
                          <span>{t.gallery?.technicalInfo || "Información técnica"}</span>
                        </span>
                        {techInfoOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>

                      {techInfoOpen && (
                        <div
                          data-testid="asset-tech-info-content"
                          className="mt-2 space-y-2 text-xs bg-[#111111] p-2.5 rounded-lg border border-[#242424]"
                        >
                          {/* Archivo original */}
                          <div>
                            <span className="text-[10px] font-mono uppercase text-[#737373] block mb-0.5">
                              {t.gallery?.originalFile || "Archivo original"}
                            </span>
                            <div className="flex items-center justify-between gap-1 font-mono text-[#d8d4cd]">
                              <span className="truncate" title={technicalName} data-testid="asset-tech-filename">
                                {technicalName || "No disponible"}
                              </span>
                              {technicalName && (
                                <button
                                  type="button"
                                  data-testid="asset-copy-filename"
                                  onClick={() => copyToClipboard(technicalName, t.gallery?.originalFile || "Nombre de archivo")}
                                  className="text-[#737373] hover:text-[#f5b544] p-1 shrink-0 transition-colors"
                                  title="Copiar nombre de archivo"
                                >
                                  <Copy size={12} />
                                </button>
                              )}
                            </div>
                          </div>

                          {/* ID (versión corta visible + copia completa) */}
                          <div>
                            <span className="text-[10px] font-mono uppercase text-[#737373] block mb-0.5">
                              {t.gallery?.assetId || "ID del recurso"}
                            </span>
                            <div className="flex items-center justify-between gap-1 font-mono text-[#a3a3a3]">
                              <span className="truncate" title={asset.id} data-testid="asset-short-id">
                                {formatShortId(asset.id)}
                              </span>
                              <button
                                type="button"
                                data-testid="asset-copy-id"
                                onClick={() => copyToClipboard(asset.id, "ID")}
                                className="text-[#737373] hover:text-[#f5b544] p-1 shrink-0 transition-colors"
                                title="Copiar ID completo"
                              >
                                <Copy size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Acciones del recurso */}
                  <div className="flex flex-col gap-2 pt-2 border-t border-[#262626]">
                    {/* Ver detalles de generación (solo si fue producido por una tarea) */}
                    {linkedJob && (
                      <button
                        type="button"
                        data-testid="gallery-detail-view-job"
                        className="v-btn-primary v-btn-sm justify-center gap-1.5"
                        onClick={() => setViewJobDetail(linkedJob)}
                      >
                        <FileText size={13} />
                        <span>Ver detalles de generación</span>
                      </button>
                    )}

                    {/* Usar como referencia */}
                    <button
                      type="button"
                      data-testid="gallery-detail-use-reference"
                      className="v-btn-ghost v-btn-sm justify-center gap-1.5"
                      onClick={handleUseReference}
                    >
                      <BookmarkPlus size={13} />
                      <span>{t.gallery?.useAsReference || "Usar como referencia"}</span>
                    </button>

                    {/* Descargar archivo original */}
                    <button
                      type="button"
                      data-testid="gallery-detail-download"
                      className="v-btn-ghost v-btn-sm justify-center gap-1.5"
                      onClick={() => downloadAsset(asset)}
                    >
                      <Download size={13} />
                      <span>{t.gallery?.download || "Descargar"}</span>
                    </button>

                    {/* Abrir en editor */}
                    {onOpenInEditor && asset.projectId && (
                      <button
                        type="button"
                        data-testid="gallery-detail-open-editor"
                        className="v-btn-ghost v-btn-sm justify-center gap-1.5"
                        onClick={() => onOpenInEditor(asset)}
                      >
                        <ExternalLink size={13} />
                        <span>{t.gallery?.openEditor || "Abrir en Editor"}</span>
                      </button>
                    )}

                    {/* Upscale */}
                    {onOpenUpscale && asset.type === "image" && (
                      <button
                        type="button"
                        data-testid="gallery-detail-upscale"
                        className="v-btn-ghost v-btn-sm justify-center gap-1.5"
                        onClick={() => onOpenUpscale(asset)}
                      >
                        <Sparkles size={13} />
                        <span>{t.gallery?.upscale || "Mejorar calidad (Upscale)"}</span>
                      </button>
                    )}

                    {/* Quitar / Eliminar (si corresponde) */}
                    {onDelete && (
                      <button
                        type="button"
                        data-testid="gallery-detail-delete"
                        className="v-btn-ghost v-btn-sm justify-center gap-1.5 border-red-500/40 text-red-400 hover:border-red-500 hover:text-red-300 mt-2"
                        onClick={() => onDelete(asset)}
                      >
                        <Trash2 size={13} />
                        <span>Quitar / Eliminar…</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal anidado de detalles de generación si el usuario pulsa "Ver detalles de generación" */}
      {viewJobDetail && (
        <JobDetailModal
          job={viewJobDetail}
          open={Boolean(viewJobDetail)}
          onOpenChange={(isOpen) => !isOpen && setViewJobDetail(null)}
          assetById={assetById}
          projectById={projectById}
          sceneById={sceneById}
          lang="es"
        />
      )}
    </>
  );
}
